<template>
    <div class="relative" style="min-width: 738px;">
        <div class="mb-5 mr-4 pb-2 flex justify-between border-b border-gray-300">
            <div v-if="showHeader" class="flex items-center">
                <h1 class="module-header">Comps</h1>
                <div class="ml-3 flex items-center text-gray-600">
                    <div class="text-2xl">/</div>
                    <div class="ml-2 pt-1 text-xl">{{ projectComps.filtered.length }} of {{ projectComps.all.length }}</div>
                </div>
            </div>
            <div v-if="showFilters" class="flex items-center">
                <filters
                    data-type="projectComps"
                    sort-by="pivot.favorited_at, pivot.score"
                    :show-client-filter="false"
                    :filters="filters"
                    :selected-view-option="layout"
                    portal="no-comps-found"
                    @view-change="layout = $event"
                >
                    <template slot="filters">
                        <div class="flex items-center mb-4">
                            <div class="flex flex-col leading-normal">
                                <span>Available Sq Ft</span>
                                <div v-if="availableSqFtFilter > 0" class="font-semibold">&gt;= {{ availableSqFtFilter | number }}</div>
                            </div>
                            <input v-model="availableSqFtFilter" type="range" min="0" max="1000000" step="10000" value="0" class="range-green">
                        </div>
                        <div class="flex items-center">
                            <span>For Sale</span>
                            <toggle-button v-model="forSaleFilter" color="#38C172" sync></toggle-button>
                        </div>
                    </template>
                </filters>
                <slot name="action-buttons">
                    <button v-if="showAddComps && canManageComps" @click="$modal.show('project-comps')" class="ml-6 flex items-center btn btn-small btn-indigo">
                        <inline-svg name="edit" classes="w-4 h-4 text-white fill-current"></inline-svg>
                        <span class="ml-2">Manage Comps</span>
                    </button>
                </slot>
            </div>
        </div>

        <div v-if="layout == 'grid'">
            <paginate name="comps" :list="projectComps.filtered" :per="perPage" class="paginate-list">
                <div class="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-6" :class="{ 'mx-auto': centerCards }">
                    <div
                        v-for="(comp, index) in paginated('comps')" :key="comp.id"
                        class="card h-full relative flex flex-col items-stretch text-gray-800 border rounded-lg shadow-lg cursor-pointer transform hover:scale-105"
                    >
                        <a
                            :key="comp.id"
                            class="flex-1 hover:no-underline"
                            :href="comp.pivot.view_url"
                        >
                            <div class="w-full h-full flex">
                                <div class="w-full flex flex-col">
                                    <div class="flex flex-1">
                                        <div class="absolute top-0 right-0 badge border rounded rounded-br-none" :class="comp.pivot.score_badge_color">
                                            <div class="text-sm font-semibold">{{ comp.pivot.score }}%</div>
                                        </div>
                                        <div class="flex w-full">
                                            <div class="w-40 h-full relative text-white">
                                                <img class="w-full h-full border-r border-white rounded-tl-lg" :src="comp.image.view_url">
                                                <inline-svg name="image-skewer-right"></inline-svg>
                                            </div>
                                            <div class="mx-4 mt-12 mb-10 flex flex-1 flex-col justify-center">
                                                <div class="uppercase">
                                                    <p v-if="! isNegotiationMode" class="text-lg font-semibold" :title="comp.address_line_1">{{ comp.address_line_1 | truncate(25) }}</p>
                                                    <p v-else class="text-lg font-semibold">Alternative Site #{{ index + 1 }}</p>
                                                    <p class="text-sm">{{ comp.city }}, {{ comp.state }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </a>

                        <div class="py-4 px-5 flex items-center justify-between bg-gray-100">
                            <comp-action-buttons :comp-data="comp"></comp-action-buttons>

                            <div class="badge badge-green">
                                <div class="flex items-baseline space-x-1">
                                    <span data-precision="2" :data-originalvalue="comp.pivot.ask_price" class="is-currency font-extrabold">{{ formatCurrency(comp.pivot.ask_price, 2) }}</span>
                                    <span class="text-base font-variant-all-small-caps">Ask Price</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </paginate>
            <paginate-links for="comps" :show-step-links="true" :hide-single-page="true"></paginate-links>
        </div>

        <div v-if="layout == 'table' && projectComps.filtered.length">
            <table class="w-full mb-6">
                <thead class="cursor-pointer">
                    <tr>
                        <th></th>
                        <th class="hidden lg:table-cell"></th>
                        <th class="block pl-5">Address</th>
                        <th class="text-center">
                            <div class="inline-flex items-center space-x-1 justify-center">
                                <span>Score</span>
                                <InfoIcon identifier="inside-a-project-comps-score" />
                            </div>
                        </th>
                        <th class="hidden lg:table-cell">Type</th>
                        <th class="hidden lg:table-cell text-center">Floors</th>
                        <th class="hidden lg:table-cell text-right">Sq. Ft.</th>
                        <th class="text-right">Avail Sq. Ft.</th>
                        <th class="text-right"># Units</th>
                        <th class="text-right">Ask Price</th>
                    </tr>
                </thead>
                <paginate name="comps" :list="projectComps.filtered" :per="perPage" tag="tbody">
                    <tr v-for="(comp, index) in paginated('comps')" :key="comp.id">
                        <td>
                            <comp-action-buttons :comp-data="comp"></comp-action-buttons>
                        </td>
                        <td class="hidden lg:table-cell text-center w-16 h-16">
                            <a :href="`#${comp.id}`">
                                <img :src="comp.image.view_url" class="mx-auto w-12 h-12 object-cover rounded-md shadow-md">
                            </a>
                            <a href="#_" class="lightbox" :id="`${comp.id}`">
                                <img :src="comp.image.view_url">
                            </a>
                        </td>
                        <td class="leading-normal">
                            <a v-bind:href="comp.pivot.view_url" class="block ml-5">
                                <p v-if="! isNegotiationMode" class="text-sm font-semibold">{{ comp.address_line_1 }}</p>
                                <p v-else class="text-sm font-semibold">Alternative Site #{{ index + 1 }}</p>
                                <p class="text-xs">{{ comp.city }}, {{ comp.state }}</p>
                            </a>
                        </td>
                        <td>
                            <div
                                class="flex items-center justify-center w-10 h-10 p-4 rounded-full shadow-lg font-medium text-base text-white"
                                :class="comp.pivot.score_color">
                                {{ comp.pivot.score }}
                            </div>
                        </td>
                        <td class="hidden lg:table-cell">{{ comp.type.type }}</td>
                        <td class="hidden lg:table-cell text-center">{{ comp.floors }}</td>
                        <td class="hidden lg:table-cell text-right">{{ comp.total_square_footage | number }}</td>
                        <td class="text-right">{{ comp.available_square_footage | number }}</td>
                        <td class="text-right">{{ comp.units.length | number }}</td>
                        <td data-precision="2" :data-originalvalue="comp.pivot.ask_price" class="is-currency text-right">{{ formatCurrency(comp.pivot.ask_price, 2) }}</td>
                    </tr>
                </paginate>
            </table>
            <paginate-links for="comps" :show-step-links="true" :hide-single-page="true"></paginate-links>
        </div>

        <div v-if="layout == 'map'" class="w-full h-full pr-5" style="min-height: 700px;">
            <div id="comp_table_map" class="h-full" style="min-height: 700px;">
                <div id="comp_table_infoBubble" class="hidden bg-white max-w-md"></div>
            </div>
        </div>

        <div v-if="! projectComps.filtered.length">
            <p class="none-found">No Comps Found</p>
        </div>

        <modal name="project-comps" width="65%" height="auto" :clickToClose="true" :scrollable="true" draggable=".modal-header">
            <div>
                <manage-project-comps
                    :project-id="projectId"
                    :comp-data="projectComps.all"
                    small-header>
                </manage-project-comps>
            </div>
        </modal>

        <comp-modal :project="projects.selectedProject" :comp-id="selectedComp.pivot?.id"></comp-modal>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import { Loader } from "@googlemaps/js-api-loader";

    export default {
        props: {
            projectId: {
                type: Number,
                required: true
            },

            compData: {
              type: Array,
              required: true
            },

            showHeader: {
              type: Boolean,
              default: true
            },

            showFilters: {
              type: Boolean,
              default: true
            },

            showAddComps: {
              type: Boolean,
              default: false
            },

            centerCards: {
                type: Boolean,
                default: false
            },

            view: {
                type: String,
                default: 'table'
            },

            perPage: {
                type: Number,
                default: 10
            }
        },

        data() {
            return {
                paginate: ['comps'],
                availableSqFtFilter: '',
                forSaleFilter: false,
                layout: this.view,
                loader: null,
                map: null,
                markers: [],
                center: { lat: 39.8097343, lng: -98.5556199 },
                infoWindow: null,
                featureData: {},
                selectedComp: {},
                rotation: 0,
                zoom: 4
            }
        },

        mounted() {
            if (localStorage.getItem('reopt_projectComps_layout')) {
                this.layout = localStorage.getItem('reopt_projectComps_layout');
            }

            this.projectComps.all = this.compData
            this.projectComps.filtered = this.compData
            this.projects.selectedProject = _.find(this.projects.all, ['id', this.projectId]);

            this.$emit('filterRequest');
        },

        watch: {
            layout() {
                if (this.layout == 'map') {
                    this.$nextTick(() => {
                        this.initMap();
                    })
                }
            },

            'ksds.selectedScenarioId'() {
                this.recalculateCompScores();
            }
        },

        computed: {
            ...mapState(['projects']),
            ...mapState(['projectComps']),
            ...mapState(['ksds']),
            ...mapGetters(['scenario', 'scenarioKsds']),

            canManageComps() {
                return this.$gate.can('update', 'project', this.projectId);
            },
            isNegotiationMode() {
                return this.scenario.negotiation_mode;
            },

            filters() {
                return {
                    availableSqFtFilter: this.availableSqFtFilter,
                    forSaleFilter: this.forSaleFilter
                }
            }
        },

        methods: {
            async initMap() {
                this.loader = new Loader({
                  apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
                  version: "quarterly"
                });

                this.loader.load().then(async () => {
                  const { Map, InfoWindow } = await google.maps.importLibrary("maps");

                  this.map = new Map(document.getElementById("comp_table_map"), {
                    center: this.center,
                    zoom: this.zoom,
                    mapId: "REOPTIMIZER_REATLAS_V2",
                  });

                  this.infoWindow = new InfoWindow();

                  window.showComp = () => { this.showComp(); }
                });

                await this.addMarkers(this.projectComps.filtered);
            },

            async addMarkers(data) {
                await _.each(data, (row) => { this.addMarker(row); });
            },

            async addMarker(item, type) {
                if (item.longitude == null || item.latitude == null) return;

                const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

                let icon = document.createElement("img");
                icon.src = this.getCompMarker(item);

                let marker = new AdvancedMarkerElement({
                    map: this.map,
                    position: { lat: item.latitude, lng: item.longitude },
                    content: icon,
                });

                marker.addListener("click", ({ domEvent, latLng }) => {
                    this.featureData = item;

                    this.infoWindow.close();
                    this.infoWindow.setContent(this.generateCompInfoBubble());
                    this.infoWindow.open(marker.map, marker);
                });

                this.markers.push(marker);
            },

            centerWithBounds() {
                let bounds = new google.maps.LatLngBounds();

                this.markers.forEach(marker => {
                    bounds.extend(marker.position);
                    this.map.fitBounds(bounds);
                })
            },

            generateCompInfoBubble() {
                return `
                    <div class="p-4 flex items-center justify-between bg-gray-50 border-b border-gray-100">
                        <div>
                            <p class="text-lg font-semibold">${this.featureData.address_line_1}</p>
                            <p class="text-sm">
                                ${this.featureData.city}, ${this.featureData.state}
                            </p>
                        </div>
                        <div class="flex items-center">
                            <div class="ml-10 badge p-6 w-8 h-8 flex justify-center items-center text-white font-bold rounded-md shadow-md ${this.featureData.pivot.score_color}" title="Score">
                               ${ this.featureData.pivot.score }%
                            </div>
                        </div>
                    </div>
                    <div class="p-3 flex flex-col">
                        <div class="flex justify-between">
                            <div class="flex flex-1 flex-col">
                                <img class="w-full h-full object-cover border-r border-white rounded-md shadow-md" src="${this.featureData.image.view_url}">
                                <button onclick="showComp()" type="button" class="open-comp mt-5 mx-auto btn btn-small btn-green">View Comp</button>
                            </div>
                            <div class="flex flex-1 justify-end">
                                <table>
                                    <tbody>
                                        <tr>
                                            <td class="pl-2 text-sm font-semibold">Comp Size</td>
                                            <td class="text-sm text-right">${this.formatNumber(this.featureData.total_square_footage)}</td>
                                        </tr>
                                        <tr>
                                            <td class="pl-2 text-sm font-semibold">Ask Price PSF</td>
                                            <td data-precision="2" data-originalvalue="${this.featureData.pivot.ask_price}" class="is-currency text-sm text-right">${this.formatCurrency(this.featureData.pivot.ask_price, 2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                `;
            },

            showComp() {
                this.selectedComp = this.featureData;

                this.$nextTick(() => {
                    this.$modal.show('comp-modal');
                });
            },

            getCompMarker(comp) {
                let favorited = ( comp.pivot.is_favorite ) ? "-favorite" : "";

                if ( comp.pivot.score == 0 && !comp.isRejected ) {
                    return '/img/map/marker-comp-unresearched' + favorited + '.png';
                } else if ( comp.pivot.is_rejected ) {
                    return '/img/map/marker-comp-rejected' + favorited + '.png';
                } else if ( comp.pivot.score < 70 ) {
                    return '/img/map/marker-comp-rejected' + favorited + '.png';
                } else {
                    if(comp.pivot.score >= 90)
                        return '/img/map/marker-comp-a' + favorited + '.png';
                    else
                        return '/img/map/marker-comp-b' + favorited + '.png';
                }
            },

            recalculateCompScores() {
                _.each(this.projectComps.all, comp => {
                    comp.pivot.score = this.calculateCompScore(comp.pivot.id);
                    comp.pivot.score_badge_color = this.getScoreBadgeColor(comp.pivot.score);
                    comp.pivot.score_color = this.getScoreColor(comp.pivot.score);
                });
            },

            calculateCompScore(compId) {
                var compScore = 0;
                const priceKSD = _.find(this.scenarioKsds, ['driver', 'Price']);
                const priceWeight = priceKSD ? parseInt(priceKSD.weight) : 0;

                _.map(this.scenarioKsds, ksd => {
                    let scoreRecord = _.find(ksd.scores, ['scorable_id', compId]);
                    const score = scoreRecord ? parseInt(scoreRecord['score']) : 0;
                    const weight = parseInt(ksd.weight);
                    const divisble = 100 - priceWeight;

                    const scorePercentage = score * 20;
                    const weightedScore = (scorePercentage * weight) / divisble;

                    compScore += weightedScore;
                });

                compScore = Math.round(compScore);

                return compScore > 100 ? 100 : compScore;
            },

            getScoreColor(score) {
                return (score < 70 ? 'bg-red-500' : (score >= 90 ? 'bg-green-500' : 'bg-yellow-200 text-yellow-700'));
            },

            getScoreBadgeColor(score) {
                if (score < 70) {
                    return 'badge-red';
                }

                if (score >= 90) {
                    return 'badge-green';
                }

                if (score >= 70) {
                    return 'badge-yellow';
                }

                return 'badge-red';
            }
        }
    }
</script>
